type SpeechTranscriptionCallbacks = {
  onListeningStart?: (ev: Event) => void;
  onListeningStop?: (ev: Event) => void;
  onError?: (error: string) => void;
  onAudioAvailable: (blob: Blob, sampleDuration?: number) => void;
};

export class SpeechTranscription {
  private mediaRecorder?: MediaRecorder;
  private isListening: boolean = false;
  private callbacks: SpeechTranscriptionCallbacks = {
    onListeningStart() { },
    onListeningStop() { },
    onError() { },
    onAudioAvailable() { },
  };
  private debounceTimeout?: NodeJS.Timeout

  constructor(private audioThresholdDuration: number = 0.5) { }

  public initialize() {
    if (!this.mediaRecorder) {
      navigator.mediaDevices
        .getUserMedia({ audio: true })
        .then((stream) => {
          this.mediaRecorder = new MediaRecorder(stream, { mimeType: 'audio/webm;codecs=opus' });

          this.mediaRecorder.onstart = (ev) => {
            this.isListening = true;
            this.callbacks.onListeningStart?.(ev);
          };

          this.mediaRecorder.onstop = (ev) => {
            this.isListening = false;
            this.callbacks.onListeningStop?.(ev);
          };

          this.mediaRecorder.ondataavailable = (ev) => {
            if (!ev.data) return;
            if (this.mediaRecorder!.audioBitsPerSecond) {
              const sampleDuration = ev.data.size / (this.mediaRecorder!.audioBitsPerSecond / 8)
              if (sampleDuration > this.audioThresholdDuration) {
                this.callbacks.onAudioAvailable(ev.data, sampleDuration);
              }
            } else {
              this.callbacks.onAudioAvailable(ev.data);
            }
          };

          this.mediaRecorder.onerror = (ev) => {
            console.trace(ev);
            this.callbacks.onError?.('Something went wrong.');
          };
        })
        .catch((err) => {
          console.trace(err);
          this.callbacks.onError?.(err.message);
          console.error('Error in getting microphone permissions:', err);
        });
    }
  }

  public setCallbacks(callbacks: SpeechTranscriptionCallbacks) {
    this.callbacks = {
      ...this.callbacks,
      ...callbacks,
    };
  }

  public startRecording() {
    if (!this.isListening && this.mediaRecorder && this.mediaRecorder.state !== 'recording') {
      this.mediaRecorder.start();
      console.log("SpeechTranscription::recording")
    }
  }

  public stopRecording() {
    if (this.isListening && this.mediaRecorder && this.mediaRecorder.state === 'recording') {
      this.mediaRecorder.stop();
      console.log("SpeechTranscription::stopped")
    }
  }

  public setupKeyboardControls(windowEl: Window | Document, keyCode: string, signal?: AbortSignal) {
    const handleKeyDown = (event: any) => {
      if (event.target instanceof HTMLInputElement || event.target instanceof HTMLTextAreaElement) {
        return
      }

      console.log('KeyDown Event >', event)

      // are any of the modifier keys pressed ?
      const isModPressed = event.shiftKey || event.altKey || event.ctrlKey || event.metaKey

      if (event.code === keyCode && !isModPressed && !event.repeat && !this.isListening) {
        event.preventDefault();

        if (this.debounceTimeout) clearTimeout(this.debounceTimeout);

        const debounceFn = () => {
          this.startRecording();
          console.log('setupKeyboardControls::handleKeyDown::timeout', this.debounceTimeout)
          clearTimeout(this.debounceTimeout)
          this.debounceTimeout = undefined;
        }

        this.debounceTimeout = setTimeout(debounceFn, 100);
      }
    };

    const handleKeyUp = (event: any) => {
      if (event.target instanceof HTMLInputElement || event.target instanceof HTMLTextAreaElement) {
        if (event.target.value.trim()) return
      }

      console.log('KeyUp Event >', event)

      if (event.code === keyCode) {
        if (this.debounceTimeout)
          clearTimeout(this.debounceTimeout);
        this.stopRecording();
      }
    };

    windowEl.addEventListener('keydown', handleKeyDown, { signal });
    windowEl.addEventListener('keyup', handleKeyUp, { signal });
  }

  public teardown() {
    if (this.mediaRecorder) {
      this.mediaRecorder.stop();
      this.mediaRecorder = undefined;
    }
  }
}
